import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { DataManager } from "@/components/DataManager";
import { PageHeader, StatusBadge, DaysBadge } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Award, Mail, Loader2, Search, ShieldCheck, Star } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const daysUntil = (d) => { if (!d) return null; return Math.ceil((new Date(d) - new Date()) / 86400000); };

// ------- Field configs per category -------
const PHARMACY_FIELDS = [
  { name: "license_type", label: "License / Registration Type", required: true, type: "select", options: [
    "State Pharmacy Facility License", "DEA Registration", "NPI Registration", "NCPDP Provider ID",
    "LLC / Business Registration", "Controlled Substance License", "Sales & Use Tax Permit", "City Business License", "Other"
  ] },
  { name: "holder_name", label: "Entity / DBA", required: true, default: "" },
  { name: "license_number", label: "License / Registration Number", mono: true, required: true },
  { name: "issuing_state", label: "Issuing State / Jurisdiction" },
  { name: "issuing_authority", label: "Issuing Authority" },
  { name: "expiry_date", label: "Expiration Date", type: "date", required: true },
  { name: "status", label: "Status", type: "select", options: ["active", "expired", "pending"], default: "active" },
  { name: "notes", label: "Notes", type: "textarea", full: true },
  { name: "attachment", label: "Attach Certificate / Registration (PDF or image)", type: "file", full: true },
];

const TECH_FIELDS = [
  { name: "holder_name", label: "Technician Name", required: true },
  { name: "license_type", label: "Credential Type", required: true, type: "select", options: [
    "State Pharmacy Technician License", "CPhT (PTCB)", "ExCPT (NHA)", "Sterile Compounding Certification",
    "Hazardous Drug Handling Certification", "IV Certification", "Immunization Certification", "Other"
  ] },
  { name: "license_number", label: "License / Certification #", mono: true, required: true },
  { name: "issuing_state", label: "Issuing State" },
  { name: "issuing_authority", label: "Issuing Authority" },
  { name: "expiry_date", label: "Expiration Date", type: "date", required: true },
  { name: "status", label: "Status", type: "select", options: ["active", "expired", "pending"], default: "active" },
  { name: "notes", label: "Notes", type: "textarea", full: true },
  { name: "attachment", label: "Attach Certificate (PDF or image)", type: "file", full: true },
];

const PHARMACIST_FIELDS = [
  { name: "holder_name", label: "Pharmacist Name", required: true },
  { name: "license_type", label: "License Type", required: true, type: "select", options: [
    "State Pharmacist License (Home State)", "State Pharmacist License (Out-of-State)", "DEA Registration",
    "Board Certification (BCPS/BCSCP/BCACP etc.)", "Immunization Certification", "MTM Certification",
    "Non-Sterile Compounding Certification", "Sterile Compounding Certification", "Other"
  ] },
  { name: "license_number", label: "License Number", mono: true, required: true },
  { name: "issuing_state", label: "Issuing State" },
  { name: "issuing_authority", label: "Issuing Authority" },
  { name: "expiry_date", label: "Expiration Date", type: "date", required: true },
  { name: "status", label: "Status", type: "select", options: ["active", "expired", "pending"], default: "active" },
  { name: "notes", label: "Notes", type: "textarea", full: true },
  { name: "attachment", label: "Attach License (PDF or image)", type: "file", full: true },
];

const CATEGORY_TESTS = {
  pharmacy: (r) => {
    const t = (r.license_type || "").toLowerCase();
    return t.includes("facility") || t.includes("dea registration") || t.includes("npi") ||
           t.includes("ncpdp") || t.includes("llc") || t.includes("business") || t.includes("tax permit") ||
           t.includes("controlled substance license") || t.includes("dea");
  },
  pharmacist: (r) => {
    const t = (r.license_type || "").toLowerCase();
    return t.includes("pharmacist") || t.includes("board certification") ||
           t.includes("mtm") || t.includes("bcp") || t.includes("bcsc") ||
           (t.includes("compounding") && !t.includes("hazardous")) ||
           (t.includes("immunization") && !t.includes("technician"));
  },
  technician: (r) => {
    const t = (r.license_type || "").toLowerCase();
    return t.includes("technician") || t.includes("cpht") || t.includes("excpt") ||
           t.includes("hazardous drug") || t.includes("iv certification");
  },
};

function LicenseSection({ tabKey, testidPrefix, fields, items, reload, emptyTitle, extraColumns = [] }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const scoped = items.filter((r) => CATEGORY_TESTS[tabKey](r));
    if (!needle) return scoped;
    return scoped.filter((r) => [r.license_type, r.holder_name, r.license_number, r.issuing_state, r.issuing_authority, r.notes]
      .some((v) => String(v || "").toLowerCase().includes(needle)));
  }, [items, q, tabKey]);

  const baseColumns = [
    { name: "license_type", label: "Type", render: (r) => <span className="font-medium text-slate-800">{r.license_type}</span> },
    { name: "holder_name", label: tabKey === "pharmacy" ? "Entity" : "Holder" },
    { name: "license_number", label: "Number", mono: true },
    { name: "issuing_state", label: "State" },
    { name: "expiry_date", label: "Expires", render: (r) => <span className="font-mono text-xs">{r.expiry_date || "—"}</span> },
    { name: "days", label: "Countdown", render: (r) => <DaysBadge days={daysUntil(r.expiry_date)} /> },
    { name: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    ...extraColumns,
  ];

  return (
    <div>
      <div className="relative max-w-md mb-3">
        <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <Input data-testid={`${testidPrefix}-search`} placeholder="Search this tab…" value={q}
          onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" />
      </div>
      <DataManager testidPrefix={testidPrefix} endpoint="licenses" fields={fields} columns={baseColumns}
        items={filtered} reload={reload} emptyIcon={Award} emptyTitle={emptyTitle} />
    </div>
  );
}

export default function Licenses() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [sending, setSending] = useState(false);

  const reload = useCallback(() => api.get("/licenses").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);

  const sendReminders = async () => {
    setSending(true);
    try { const { data } = await api.post("/reminders/send-now"); toast.success(`Reminder sent — ${data.items} item(s) flagged`); }
    catch (e) { toast.error("Could not send reminders"); }
    finally { setSending(false); }
  };

  const markPIC = async (id) => {
    try { await api.post(`/licenses/${id}/set-pic`); toast.success("Marked as Pharmacist-in-Charge"); reload(); }
    catch (e) { toast.error("Could not update PIC"); }
  };

  const pharmacistExtraColumns = [
    { name: "is_pic", label: "PIC", render: (r) => (
      r.is_pic ? (
        <span className="inline-flex items-center gap-1 bg-pink-50 text-pink-800 px-2 py-0.5 rounded-full text-xs font-semibold">
          <Star className="w-3 h-3 fill-pink-700 text-pink-700" /> PIC
        </span>
      ) : isAdmin ? (
        <Button data-testid={`pharmacist-mark-pic-${r.id}`} size="sm" variant="ghost"
          className="h-7 text-xs text-slate-500 hover:text-pink-800" onClick={() => markPIC(r.id)}>
          Mark PIC
        </Button>
      ) : <span className="text-slate-300 text-xs">—</span>
    ) },
  ];

  return (
    <div>
      <PageHeader testid="licenses-header" title="License Tracking"
        subtitle="Pharmacy, pharmacist and technician credentials organized by category with countdowns.">
        {isAdmin && <Button data-testid="send-reminders-button" onClick={sendReminders} disabled={sending} variant="outline" className="gap-1.5 border-pink-300 text-pink-800">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Email Reminders
        </Button>}
      </PageHeader>

      <Tabs defaultValue="pharmacy" className="w-full">
        <TabsList data-testid="licenses-tabs" className="h-10 bg-slate-100 p-1">
          <TabsTrigger data-testid="licenses-tab-pharmacy" value="pharmacy" className="gap-1.5"><ShieldCheck className="w-4 h-4" /> Pharmacy</TabsTrigger>
          <TabsTrigger data-testid="licenses-tab-pharmacist" value="pharmacist" className="gap-1.5"><Award className="w-4 h-4" /> Pharmacists</TabsTrigger>
          <TabsTrigger data-testid="licenses-tab-technician" value="technician" className="gap-1.5"><Award className="w-4 h-4" /> Technicians</TabsTrigger>
        </TabsList>
        <TabsContent value="pharmacy" className="mt-4">
          <LicenseSection tabKey="pharmacy" testidPrefix="pharmacy-licenses" fields={PHARMACY_FIELDS}
            items={items} reload={reload} emptyTitle="No pharmacy licenses tracked yet" />
        </TabsContent>
        <TabsContent value="pharmacist" className="mt-4">
          <LicenseSection tabKey="pharmacist" testidPrefix="pharmacist-licenses" fields={PHARMACIST_FIELDS}
            items={items} reload={reload} emptyTitle="No pharmacist licenses tracked yet"
            extraColumns={pharmacistExtraColumns} />
        </TabsContent>
        <TabsContent value="technician" className="mt-4">
          <LicenseSection tabKey="technician" testidPrefix="technician-licenses" fields={TECH_FIELDS}
            items={items} reload={reload} emptyTitle="No technician credentials tracked yet" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
